import { useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Action, projectSubject, useAppAbility } from "common/access";
import { useAuth } from "contexts/AuthContext";
import { ReviewPageV3 } from "./ReviewPageV3";
import {
  applyHistoryChangesToDiagram,
  applyHistoryChangesToPlan,
  historyKeys,
} from "./HistoryDrawer";
import { updatePlanElement } from "./utils/updatePlanElement";
import { removePlanElement } from "./utils/removePlanElement";
import { removeElementGroupFromPlan } from "./utils/removeElementGroupFromPlan";
import { bulkAddPlanElements } from "./utils/bulkAddPlanElements";
import { bulkRemovePlanElements } from "./utils/bulkRemovePlanElements";
import { bulkMovePlanElements } from "./utils/bulkMovePlanElements";
import { bulkAddPlanRoutes } from "./utils/bulkAddPlanRoutes";
import {
  addSelectionAreaToPlan,
  removeSelectionAreaFromPlan,
  updateSelectionAreaInPlan,
} from "./utils/selectionAreas";
import { applyPageCalibrationToPlan } from "./utils/applyPageCalibrationToPlan";
import { applyPageRotationToPlan } from "./utils/applyPageRotationToPlan";
import { renameGroupInPlan } from "./utils/renameGroupInPlan";
import { removeRouteFromPlan } from "./utils/removeRouteFromPlan";
import { removeRouteGroupFromPlan } from "./utils/removeRouteGroupFromPlan";
import { renameRouteGroupInPlan } from "./utils/renameRouteGroupInPlan";
import { moveRouteBetweenGroups } from "./utils/moveRouteBetweenGroups";
import { updateRouteGeometryInPlan } from "./utils/updateRouteGeometryInPlan";
import { setElementGroupLvMatchInPlan } from "./utils/setElementGroupLvMatchInPlan";
import { setRouteGroupLvMatchInPlan } from "./utils/setRouteGroupLvMatchInPlan";
import { setBoardItemLvMatchInDiagram } from "./utils/setBoardItemLvMatchInDiagram";
import { removeBoardItemGroupFromDiagram } from "./utils/removeBoardItemGroupFromDiagram";
import { markElementTypesReviewedInPlan } from "./utils/markElementTypesReviewedInPlan";
import { markRouteTypesReviewedInPlan } from "./utils/markRouteTypesReviewedInPlan";
import { setElementTypeNameStatusInPlan } from "./utils/setElementTypeNameStatusInPlan";
import { setCableTypeNameStatusInPlan } from "./utils/setCableTypeNameStatusInPlan";
import { useProjectMetadata } from "./hooks/useProjectMetadata";
import { useLazyPlanLoader } from "./hooks/useLazyPlanLoader";
import { useFloorPlanFolderRecords } from "./hooks/useFloorPlanFolderRecords";
import { useLazyDiagramLoader } from "./hooks/useLazyDiagramLoader";
import type {
  UpdateFloorPlanElement,
  DeleteFloorPlanElement,
  DeleteFloorPlanElementGroup,
  BulkCreateFloorPlanElements,
  BulkDeleteFloorPlanElements,
  BulkMoveFloorPlanElements,
  BulkCreateFloorPlanRoutes,
  AddFloorPlanSelectionArea,
  UpdateFloorPlanSelectionArea,
  DeleteFloorPlanSelectionArea,
  RenameGroups,
  RenamePlan,
  DeleteRoute,
  DeleteRouteGroup,
  RenameRouteGroups,
  UpdateRoute,
  UpdateRouteGeometry,
  SetFloorPlanPageCalibration,
  SetFloorPlanPageRotation,
  SetFloorPlanElementGroupLvMatch,
  SetFloorPlanGroupsLvMatch,
  SetFloorPlanRouteGroupLvMatch,
  SetBoardItemLvMatch,
  DeleteBoardItemGroup,
  MarkElementTypesReviewed,
  MarkRouteTypesReviewed,
  SetElementTypeNameStatus,
  SetCableTypeNameStatus,
  ApplyHistoryChanges,
} from "./types";
import type { FloorPlanFolderImportResult } from "./utils/types";

export const ReviewPageWrapperV3 = () => {
  const navigate = useNavigate();
  const { id: projectId = "" } = useParams();
  const ability = useAppAbility();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const isSuperAdmin = !!user?.isGlobalAdmin;

  const {
    isLoading: isMetadataLoading,
    metadata,
    error,
  } = useProjectMetadata({
    projectId,
    isSuperAdmin,
    onUploadNotReady: () =>
      navigate(`/project/${projectId}/smart-surveyor/upload`),
  });

  // Every recorded write adds an entry and can change what older entries may revert.
  const invalidateHistory = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: historyKeys.all });
  }, [queryClient]);

  // Members are read-only (view + export); managers and admins edit.
  const canEdit = useMemo(
    () =>
      ability.can(
        Action.Update,
        projectSubject({
          id: metadata?.project?.id,
          organizationId: metadata?.project?.organizationId,
        }),
      ),
    [ability, metadata],
  );

  // Pricing is a feature of the organization; the rule arrives with the user's packed rules.
  const canManagePrices = useMemo(
    () =>
      ability.can(
        Action.ManagePrices,
        projectSubject({
          id: metadata?.project?.id,
          organizationId: metadata?.project?.organizationId,
        }),
      ),
    [ability, metadata],
  );

  // Managers can archive but not delete; deletion is admin-level.
  const canDelete = useMemo(
    () =>
      ability.can(
        Action.Delete,
        projectSubject({
          id: metadata?.project?.id,
          organizationId: metadata?.project?.organizationId,
        }),
      ),
    [ability, metadata],
  );

  const {
    isInitialLoading: isPlanInitialLoading,
    planRecords,
    selectedPlanRecordIndex,
    pendingPlanRecordIndex,
    selectedPlan,
    selectPlanRecord,
    clearPlanSelection,
    updateCachedPlan,
    patchCachedPlan,
    updateCachedPlans,
    updatePlanRecordName,
    updatePlanRecordArchivedAt,
    updatePlanRecordFolderId,
    setPlanRecordFolderIds,
    detachPlanRecordsFromFolder,
    removePlanRecord,
  } = useLazyPlanLoader({ projectId });

  const {
    isInitialLoading: isFolderInitialLoading,
    folderRecords,
    upsertFolderRecords,
    renameFolderRecord,
    removeFolderRecord: removeFromFolderRecords,
  } = useFloorPlanFolderRecords({ projectId });

  const applyFolderImport = useCallback(
    ({ folders, planFolders }: FloorPlanFolderImportResult) => {
      upsertFolderRecords(folders);
      setPlanRecordFolderIds(planFolders);
    },
    [upsertFolderRecords, setPlanRecordFolderIds],
  );

  // A deleted folder's plans return to the root, as the DB's ON DELETE SET NULL does.
  const removeFolderRecord = useCallback(
    (folderId: number) => {
      removeFromFolderRecords(folderId);
      detachPlanRecordsFromFolder(folderId);
    },
    [removeFromFolderRecords, detachPlanRecordsFromFolder],
  );

  const {
    isInitialLoading: isDiagramInitialLoading,
    diagramRecords,
    selectedDiagramRecordIndex,
    pendingDiagramRecordIndex,
    selectedDiagram,
    selectDiagramRecord,
    loadedDiagrams,
    loadAllDiagrams,
    updateCachedDiagram,
    patchCachedDiagram,
  } = useLazyDiagramLoader({ projectId });

  const applyHistoryChanges: ApplyHistoryChanges = useCallback(
    ({ subject, changes }) => {
      if (subject.kind === "plan") {
        patchCachedPlan(subject.floorPlanId, (plan) =>
          applyHistoryChangesToPlan({ plan, changes }),
        );
        return;
      }
      patchCachedDiagram(subject.diagramId, (diagram) =>
        applyHistoryChangesToDiagram({ diagram, changes }),
      );
    },
    [patchCachedPlan, patchCachedDiagram],
  );

  const updateFloorPlanElement: UpdateFloorPlanElement = useCallback(
    ({ floorPlanId, fromGroupLabel, elementId, changes }) => {
      updateCachedPlan(floorPlanId, (plan) =>
        updatePlanElement({ plan, fromGroupLabel, elementId, changes }),
      );
      invalidateHistory();
    },
    [updateCachedPlan, invalidateHistory],
  );

  const deleteFloorPlanElement: DeleteFloorPlanElement = useCallback(
    ({ floorPlanId, groupLabel, elementId }) => {
      updateCachedPlan(floorPlanId, (plan) =>
        removePlanElement({ plan, groupLabel, elementId }),
      );
      invalidateHistory();
    },
    [updateCachedPlan, invalidateHistory],
  );

  const deleteFloorPlanElementGroup: DeleteFloorPlanElementGroup = useCallback(
    ({ floorPlanId, groupLabel }) => {
      updateCachedPlan(floorPlanId, (plan) =>
        removeElementGroupFromPlan({ plan, groupLabel }),
      );
      invalidateHistory();
    },
    [updateCachedPlan, invalidateHistory],
  );

  const bulkCreateFloorPlanElements: BulkCreateFloorPlanElements = useCallback(
    ({ floorPlanId, elements }) => {
      updateCachedPlan(floorPlanId, (plan) =>
        bulkAddPlanElements({ plan, elements }),
      );
      invalidateHistory();
    },
    [updateCachedPlan, invalidateHistory],
  );

  const bulkDeleteFloorPlanElements: BulkDeleteFloorPlanElements = useCallback(
    ({ floorPlanId, elementIds }) => {
      updateCachedPlan(floorPlanId, (plan) =>
        bulkRemovePlanElements({ plan, elementIds }),
      );
      invalidateHistory();
    },
    [updateCachedPlan, invalidateHistory],
  );

  const bulkMoveFloorPlanElements: BulkMoveFloorPlanElements = useCallback(
    ({ floorPlanId, elementIds, toGroupLabel, newGroupElementTypeId }) => {
      updateCachedPlan(floorPlanId, (plan) =>
        bulkMovePlanElements({
          plan,
          elementIds,
          toGroupLabel,
          newGroupElementTypeId,
        }),
      );
      invalidateHistory();
    },
    [updateCachedPlan, invalidateHistory],
  );

  const bulkCreateFloorPlanRoutes: BulkCreateFloorPlanRoutes = useCallback(
    ({ floorPlanId, routes }) => {
      updateCachedPlan(floorPlanId, (plan) =>
        bulkAddPlanRoutes({ plan, routes }),
      );
      invalidateHistory();
    },
    [updateCachedPlan, invalidateHistory],
  );

  const addFloorPlanSelectionArea: AddFloorPlanSelectionArea = useCallback(
    ({ floorPlanId, area }) => {
      updateCachedPlan(floorPlanId, (plan) =>
        addSelectionAreaToPlan({ plan, area }),
      );
      invalidateHistory();
    },
    [updateCachedPlan, invalidateHistory],
  );

  const updateFloorPlanSelectionArea: UpdateFloorPlanSelectionArea =
    useCallback(
      ({ floorPlanId, areaId, changes }) => {
        updateCachedPlan(floorPlanId, (plan) =>
          updateSelectionAreaInPlan({ plan, areaId, changes }),
        );
        invalidateHistory();
      },
      [updateCachedPlan, invalidateHistory],
    );

  const deleteFloorPlanSelectionArea: DeleteFloorPlanSelectionArea =
    useCallback(
      ({ floorPlanId, areaId }) => {
        updateCachedPlan(floorPlanId, (plan) =>
          removeSelectionAreaFromPlan({ plan, areaId }),
        );
        invalidateHistory();
      },
      [updateCachedPlan, invalidateHistory],
    );

  const setFloorPlanPageCalibration: SetFloorPlanPageCalibration = useCallback(
    ({ floorPlanId, pageIndex, metersPerPdfPoint, source }) => {
      updateCachedPlan(floorPlanId, (plan) =>
        applyPageCalibrationToPlan({
          plan,
          pageIndex,
          metersPerPdfPoint,
          source,
        }),
      );
      invalidateHistory();
    },
    [updateCachedPlan, invalidateHistory],
  );

  const setFloorPlanPageRotation: SetFloorPlanPageRotation = useCallback(
    ({ floorPlanId, pageIndex, rotation }) => {
      updateCachedPlan(floorPlanId, (plan) =>
        applyPageRotationToPlan({ plan, pageIndex, rotation }),
      );
      invalidateHistory();
    },
    [updateCachedPlan, invalidateHistory],
  );

  const renameGroups: RenameGroups = useCallback(
    ({ oldGroupLabel, newGroupLabel, affectedPlans }) => {
      const renamedPlanByFloorPlanId = new Map(
        affectedPlans.map((affectedPlan) => [
          affectedPlan.floorPlanId,
          affectedPlan,
        ]),
      );
      updateCachedPlans(
        affectedPlans.map((affectedPlan) => affectedPlan.floorPlanId),
        (plan) => {
          const renamedPlan = renamedPlanByFloorPlanId.get(plan.floorPlanId);
          if (!renamedPlan) return plan;
          return renameGroupInPlan({
            plan,
            oldGroupLabel,
            newGroupLabel,
            nameStatus: renamedPlan.nameStatus,
            elementTypeId: renamedPlan.elementTypeId,
          });
        },
      );
      invalidateHistory();
    },
    [updateCachedPlans, invalidateHistory],
  );

  const renamePlan: RenamePlan = useCallback(
    ({ floorPlanId, newName }) => {
      updateCachedPlan(floorPlanId, (plan) => ({ ...plan, label: newName }));
      invalidateHistory();
    },
    [updateCachedPlan, invalidateHistory],
  );

  const deleteRoute: DeleteRoute = useCallback(
    ({ floorPlanId, groupLabel, routeId }) => {
      updateCachedPlan(floorPlanId, (plan) =>
        removeRouteFromPlan({ plan, groupLabel, routeId }),
      );
      invalidateHistory();
    },
    [updateCachedPlan, invalidateHistory],
  );

  const deleteRouteGroup: DeleteRouteGroup = useCallback(
    ({ floorPlanId, groupLabel }) => {
      updateCachedPlan(floorPlanId, (plan) =>
        removeRouteGroupFromPlan({ plan, groupLabel }),
      );
      invalidateHistory();
    },
    [updateCachedPlan, invalidateHistory],
  );

  const renameRouteGroups: RenameRouteGroups = useCallback(
    ({ oldGroupLabel, newGroupLabel, affectedPlans }) => {
      const renamedPlanByFloorPlanId = new Map(
        affectedPlans.map((affectedPlan) => [
          affectedPlan.floorPlanId,
          affectedPlan,
        ]),
      );
      updateCachedPlans(
        affectedPlans.map((affectedPlan) => affectedPlan.floorPlanId),
        (plan) => {
          const renamedPlan = renamedPlanByFloorPlanId.get(plan.floorPlanId);
          if (!renamedPlan) return plan;
          return renameRouteGroupInPlan({
            plan,
            oldGroupLabel,
            newGroupLabel,
            cableTypeStatus: renamedPlan.cableTypeStatus,
            routeTypeId: renamedPlan.routeTypeId,
          });
        },
      );
      invalidateHistory();
    },
    [updateCachedPlans, invalidateHistory],
  );

  const markElementTypesReviewed: MarkElementTypesReviewed = useCallback(
    ({ floorPlanId, elementTypeIds }) => {
      updateCachedPlan(floorPlanId, (plan) =>
        markElementTypesReviewedInPlan({ plan, elementTypeIds }),
      );
      invalidateHistory();
    },
    [updateCachedPlan, invalidateHistory],
  );

  const markRouteTypesReviewed: MarkRouteTypesReviewed = useCallback(
    ({ floorPlanId, routeTypeIds }) => {
      updateCachedPlan(floorPlanId, (plan) =>
        markRouteTypesReviewedInPlan({ plan, routeTypeIds }),
      );
      invalidateHistory();
    },
    [updateCachedPlan, invalidateHistory],
  );

  const setElementTypeNameStatus: SetElementTypeNameStatus = useCallback(
    ({ floorPlanId, elementTypeIds, nameStatus }) => {
      updateCachedPlan(floorPlanId, (plan) =>
        setElementTypeNameStatusInPlan({ plan, elementTypeIds, nameStatus }),
      );
      invalidateHistory();
    },
    [updateCachedPlan, invalidateHistory],
  );

  const setCableTypeNameStatus: SetCableTypeNameStatus = useCallback(
    ({ floorPlanId, routeTypeIds, cableTypeStatus }) => {
      updateCachedPlan(floorPlanId, (plan) =>
        setCableTypeNameStatusInPlan({
          plan,
          routeTypeIds,
          cableTypeStatus,
        }),
      );
      invalidateHistory();
    },
    [updateCachedPlan, invalidateHistory],
  );

  const updateRoute: UpdateRoute = useCallback(
    ({
      floorPlanId,
      routeId,
      fromGroupLabel,
      toGroupLabel,
      newRouteTypeId,
    }) => {
      updateCachedPlan(floorPlanId, (plan) =>
        moveRouteBetweenGroups({
          plan,
          routeId,
          fromGroupLabel,
          toGroupLabel,
          newRouteTypeId,
        }),
      );
      invalidateHistory();
    },
    [updateCachedPlan, invalidateHistory],
  );

  const updateRouteGeometry: UpdateRouteGeometry = useCallback(
    ({ floorPlanId, groupLabel, routeId, nodes, edges, meters }) => {
      updateCachedPlan(floorPlanId, (plan) =>
        updateRouteGeometryInPlan({
          plan,
          groupLabel,
          routeId,
          nodes,
          edges,
          meters,
        }),
      );
      invalidateHistory();
    },
    [updateCachedPlan, invalidateHistory],
  );

  const setFloorPlanElementGroupLvMatch: SetFloorPlanElementGroupLvMatch =
    useCallback(
      ({ floorPlanId, floorPlanElementGroupLabel, lvMatch }) => {
        updateCachedPlan(floorPlanId, (plan) =>
          setElementGroupLvMatchInPlan({
            plan,
            elementGroupLabel: floorPlanElementGroupLabel,
            lvMatch,
          }),
        );
        invalidateHistory();
      },
      [updateCachedPlan, invalidateHistory],
    );

  const setFloorPlanRouteGroupLvMatch: SetFloorPlanRouteGroupLvMatch =
    useCallback(
      ({ floorPlanId, floorPlanRouteGroupLabel, lvMatch }) => {
        updateCachedPlan(floorPlanId, (plan) =>
          setRouteGroupLvMatchInPlan({
            plan,
            routeGroupLabel: floorPlanRouteGroupLabel,
            lvMatch,
          }),
        );
        invalidateHistory();
      },
      [updateCachedPlan, invalidateHistory],
    );

  const setFloorPlanGroupsLvMatch: SetFloorPlanGroupsLvMatch = useCallback(
    ({ targets, lvMatch }) => {
      const floorPlanIds = [
        ...new Set(targets.map((target) => target.floorPlanId)),
      ];
      updateCachedPlans(floorPlanIds, (plan) =>
        targets
          .filter((target) => target.floorPlanId === plan.floorPlanId)
          .reduce(
            (patchedPlan, target) =>
              target.kind === "element"
                ? setElementGroupLvMatchInPlan({
                    plan: patchedPlan,
                    elementGroupLabel: target.groupLabel,
                    lvMatch,
                  })
                : setRouteGroupLvMatchInPlan({
                    plan: patchedPlan,
                    routeGroupLabel: target.groupLabel,
                    lvMatch,
                  }),
            plan,
          ),
      );
      invalidateHistory();
    },
    [updateCachedPlans, invalidateHistory],
  );

  const setBoardItemLvMatch: SetBoardItemLvMatch = useCallback(
    ({ diagramId, diagramItemTypeId, lvMatch }) => {
      updateCachedDiagram(diagramId, (diagram) =>
        setBoardItemLvMatchInDiagram({ diagram, diagramItemTypeId, lvMatch }),
      );
      invalidateHistory();
    },
    [updateCachedDiagram, invalidateHistory],
  );

  const deleteBoardItemGroup: DeleteBoardItemGroup = useCallback(
    ({ diagramId, diagramItemTypeId }) => {
      updateCachedDiagram(diagramId, (diagram) =>
        removeBoardItemGroupFromDiagram({ diagram, diagramItemTypeId }),
      );
      invalidateHistory();
    },
    [updateCachedDiagram, invalidateHistory],
  );

  if (
    isMetadataLoading ||
    isPlanInitialLoading ||
    isFolderInitialLoading ||
    isDiagramInitialLoading
  ) {
    return (
      <div className="h-screen w-screen flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error || !metadata) {
    return null;
  }

  return (
    <ReviewPageV3
      onBack={() => navigate("/")}
      projectId={projectId}
      projectName={metadata.project?.name}
      boqExtraction={metadata.boqExtraction}
      cables={metadata.cables}
      cablesPublished={metadata.cablesPublished}
      hasResearchReport={metadata.hasResearchReport}
      onViewResearchReport={() =>
        navigate(`/project/${projectId}/smart-surveyor/research`)
      }
      canEdit={canEdit}
      canDelete={canDelete}
      isSuperAdmin={isSuperAdmin}
      canAccessCableTakeoffBeta={metadata.canAccessCableTakeoffBeta}
      canManagePrices={canManagePrices}
      planRecords={planRecords}
      selectedPlanRecordIndex={selectedPlanRecordIndex}
      pendingPlanRecordIndex={pendingPlanRecordIndex}
      selectedPlan={selectedPlan}
      selectPlanRecord={selectPlanRecord}
      clearPlanSelection={clearPlanSelection}
      updatePlanRecordName={updatePlanRecordName}
      updatePlanRecordArchivedAt={updatePlanRecordArchivedAt}
      removePlanRecord={removePlanRecord}
      folderRecords={folderRecords}
      updatePlanRecordFolderId={updatePlanRecordFolderId}
      upsertFolderRecords={upsertFolderRecords}
      renameFolderRecord={renameFolderRecord}
      removeFolderRecord={removeFolderRecord}
      applyFolderImport={applyFolderImport}
      updateFloorPlanElement={updateFloorPlanElement}
      deleteFloorPlanElement={deleteFloorPlanElement}
      deleteFloorPlanElementGroup={deleteFloorPlanElementGroup}
      bulkCreateFloorPlanElements={bulkCreateFloorPlanElements}
      bulkDeleteFloorPlanElements={bulkDeleteFloorPlanElements}
      bulkMoveFloorPlanElements={bulkMoveFloorPlanElements}
      applyHistoryChanges={applyHistoryChanges}
      invalidateHistory={invalidateHistory}
      bulkCreateFloorPlanRoutes={bulkCreateFloorPlanRoutes}
      addFloorPlanSelectionArea={addFloorPlanSelectionArea}
      updateFloorPlanSelectionArea={updateFloorPlanSelectionArea}
      deleteFloorPlanSelectionArea={deleteFloorPlanSelectionArea}
      setFloorPlanPageCalibration={setFloorPlanPageCalibration}
      setFloorPlanPageRotation={setFloorPlanPageRotation}
      renameGroups={renameGroups}
      renamePlan={renamePlan}
      deleteRoute={deleteRoute}
      deleteRouteGroup={deleteRouteGroup}
      renameRouteGroups={renameRouteGroups}
      markElementTypesReviewed={markElementTypesReviewed}
      markRouteTypesReviewed={markRouteTypesReviewed}
      setElementTypeNameStatus={setElementTypeNameStatus}
      setCableTypeNameStatus={setCableTypeNameStatus}
      updateRoute={updateRoute}
      updateRouteGeometry={updateRouteGeometry}
      diagramRecords={diagramRecords}
      selectedDiagramRecordIndex={selectedDiagramRecordIndex}
      pendingDiagramRecordIndex={pendingDiagramRecordIndex}
      selectedDiagram={selectedDiagram}
      selectDiagramRecord={selectDiagramRecord}
      loadedDiagrams={loadedDiagrams}
      loadAllDiagrams={loadAllDiagrams}
      setFloorPlanElementGroupLvMatch={setFloorPlanElementGroupLvMatch}
      setFloorPlanRouteGroupLvMatch={setFloorPlanRouteGroupLvMatch}
      setFloorPlanGroupsLvMatch={setFloorPlanGroupsLvMatch}
      setBoardItemLvMatch={setBoardItemLvMatch}
      deleteBoardItemGroup={deleteBoardItemGroup}
    />
  );
};
